import React, { useMemo, useState } from "react";
import Button from "../Button";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";
import { AnimatePresence } from "framer-motion";
import { CarouselProps } from "@models/carousel";
import { twMerge } from "tailwind-merge";

const variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
};
const Carousel = <T,>(props: CarouselProps<T>) => {
  const {
    data,
    render: Renderer,
    customBtns = false,
    btnClassName,
    buttonPosition = "center",
  } = props;
  const [[page, direction], setPage] = useState([0, 0]);

  const bodyWrapperClassName = data?.length >= 2 ? "col-span-5" : "col-span-7";

  const dataLength = useMemo(() => {
    return data?.length ?? 0;
  }, [data]);

  const dataIndex = page % dataLength;
  const selectedData = data?.[dataIndex];

  const paginate = (newDirection: number) => {
    const newPage = page + newDirection;
    if (newPage < 0) {
      setPage([dataLength - 1, newDirection]);
      return;
    }

    setPage([page + newDirection, newDirection]);
  };

  const renderLeftButton = () => {
    if (customBtns || data?.length < 2) return <></>;
    return (
      <div className="col-span-1 h-full flex items-center justify-end">
        <Button
          variant="carousel"
          onClick={() => paginate(-1)}
          className={btnClassName}
        >
          <AiOutlineArrowLeft />
        </Button>
      </div>
    );
  };

  const renderRightButton = () => {
    if (customBtns || data?.length < 2) return <></>;
    return (
      <div className="col-span-1 h-full flex items-center justify-start">
        <Button
          variant="carousel"
          className={btnClassName}
          onClick={() => paginate(1)}
        >
          <AiOutlineArrowRight />
        </Button>
      </div>
    );
  };

  if (data?.length === 0) return <></>;

  if (buttonPosition === "bottom") {
    return (
      <div className="flex flex-col gap-5 xl:gap-9">
        <Renderer
          data={selectedData}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
        />
        <div className="flex justify-center items-center w-full gap-9">
          {renderLeftButton()}
          {renderRightButton()}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-7 h-full">
      {renderLeftButton()}
      <div className={twMerge("px-8", bodyWrapperClassName)}>
        <AnimatePresence initial={false} custom={direction}>
          <Renderer
            data={selectedData}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
          />
        </AnimatePresence>
      </div>
      {renderRightButton()}
    </div>
  );
};

export default Carousel;
